import React from "react";
import Head from "next/head";
import { ArticleJsonLd } from "next-seo";
import {
    StaticMetadata,
    OpenGraph,
    TwitterMetadata,
    ParselyMetadata,
    AndroidAppLink,
    ContentMetadata as Content,
    LotameTags,
    Favicon,
    Sailthru,
} from "@foundation/metadata";

import {
    BRANDS,
    PRESS_ASSOCIATION_SHORTHAND,
    WEEKEND_NICODES,
} from "~constants";
import { Person, Story as StoryType } from "~types/types";
import { getSiteNameForLocale } from "~utils/getSiteNameForLocale";
import { getTwitterHandle } from "~utils/getTwitterHandle";
import googleArticleJsonLd from "~utils/googleJsonLd";
import isAPOrLinkedIn from "~utils/isAPOrLinkedIn";
import isPressAssociation from "~utils/isPressAssociation";

type ContentMetadataProps = {
    story: StoryType;
    type: string;
};

const OPEN_GRAPH_TYPE = "article";
const PARSELY_TYPE = "post";
const COLOR_ENV = process.env.NEXT_PUBLIC_COLOR_ENV;

export const formatTitle = (title: string, locale: string) => {
    const siteName = getSiteNameForLocale(locale);

    return `${title} - ${siteName}`;
};

export const getAuthorNames = (authors?: Person[]) =>
    authors ? authors.map((author) => author.name).join(",") : "";

export const getKeywords = (
    mostRelevantTags: string[],
    secondaryBrands: string[],
    franchise?: string
) => {
    const tags = [...mostRelevantTags, ...secondaryBrands];

    if (franchise) {
        tags.push(franchise);
    }

    return tags.join(",");
};

const getParselyAuthors = (content: StoryType): string[] => {
    if (isPressAssociation(content)) {
        return [PRESS_ASSOCIATION_SHORTHAND];
    }

    if (isAPOrLinkedIn(content)) {
        return [content.attributor as string];
    }

    if (!content.authors) {
        return [];
    }

    return content.authors.map(({ name }) => name);
};

const getParselyTags = ({
    mostRelevantTags,
    secondaryBrands = [],
    authoredRegion,
    type,
    isTrending,
    language,
    franchise,
    collections,
    nicodes,
}: StoryType) => {
    const tags = [
        ...mostRelevantTags,
        ...secondaryBrands?.map((brand) => `Secondary Brand: ${brand}`),
    ];

    if (franchise) {
        tags.push(`Franchise: ${franchise}`);
    }

    tags.push(`Region: ${authoredRegion}`);

    if (type) {
        tags.push(`Page: ${type}`);
    }

    if (isTrending) {
        tags.push("trending");
    }

    if (language && language !== "en") {
        tags.push(`Language: ${language}`);
    }

    if (collections?.weekendId) {
        tags.push(`weekend_${collections?.weekendId}`);
    }

    if (
        nicodes?.includes(WEEKEND_NICODES.all) &&
        !nicodes?.includes(WEEKEND_NICODES.curated)
    ) {
        tags.push("weekend_commissioned");
    }

    if (nicodes?.includes(WEEKEND_NICODES.curated)) {
        tags.push("weekend_curated");
    }

    return tags.join(",");
};

export function ContentMetadata(props: ContentMetadataProps) {
    const { story } = props;

    const {
        authors,
        brand,
        checksum,
        contentTags,
        facebook,
        franchise,
        headline,
        id,
        mostRelevantTags,
        publishedAt,
        secondaryBrands = [],
        seoCanonical,
        seoDescription,
        seoTitle: seoTitlePrefix,
        socialDescription,
        socialImageUrl,
        socialHeadline: socialTitle,
        twitterDescription,
        twitterTitle,
        url,
    } = story;

    const articleStructuredData = googleArticleJsonLd(story);
    const seoTitle = formatTitle(seoTitlePrefix, story.locale);
    const authorNames = getAuthorNames(authors);
    const seoTags = getKeywords(mostRelevantTags, secondaryBrands, franchise);
    const parselyTags = getParselyTags(story);
    const parselyAuthors = getParselyAuthors(story);

    // do we have a validated at flag? I dont see it in wardenclyffe
    const VALIDATED_AT = publishedAt;
    // eslint-disable-next-line new-cap
    const SailthruElements = Sailthru({
        author: authorNames,
        date: publishedAt,
        description: seoDescription ?? "",
        headline,
        keywords: seoTags,
        tags: seoTags,
        title: seoTitlePrefix,
    });

    return (
        <>
            <Head>
                <title>{seoTitle}</title>
                <Favicon brand={brand} env={COLOR_ENV} />

                <StaticMetadata
                    canonical={seoCanonical}
                    description={seoDescription}
                />
                <OpenGraph
                    description={socialDescription}
                    title={socialTitle}
                    type={OPEN_GRAPH_TYPE}
                    url={url}
                    image={socialImageUrl}
                />
                <TwitterMetadata
                    twitterHandle={getTwitterHandle(brand)}
                    description={twitterDescription}
                    title={twitterTitle}
                    image={socialImageUrl}
                />
                <ParselyMetadata
                    authors={parselyAuthors}
                    title={seoTitlePrefix}
                    link={url}
                    type={PARSELY_TYPE}
                    image={socialImageUrl}
                    publicationDate={publishedAt}
                    section={brand}
                    id={id}
                    tags={parselyTags}
                />

                <AndroidAppLink link={url} />

                {facebook && checksum && (
                    <Content
                        publishedAt={publishedAt}
                        checksum={checksum}
                        validatedAt={VALIDATED_AT}
                        isOpinion={brand === BRANDS.OPINION}
                        facebookStatus={facebook.shareText}
                    />
                )}

                {contentTags && <LotameTags wssTags={contentTags} />}
                {SailthruElements as React.ReactNode}
                {/* This is needed to signal to AbbaClient to get content meta data for this page */}
                <meta name="will-have-content-metadata" content="true" />
            </Head>
            <ArticleJsonLd {...articleStructuredData} />
        </>
    );
}
